import { Divider, Stack, Typography } from '@mui/material';
import { date as yupDate, object } from 'yup';
import { CheckinDate } from '../../lib/types';
import { localDateKey } from '../../lib/dates';
import { useFirebaseContext } from '../../lib/firebase-context';

// Yup schema for the date dialog. `existingDates` (and the optional
// `excludeRef` for edit mode) drive the uniqueness check; only one
// check-in-date doc may exist per local calendar day per event.
export const buildSchema = (
  existingDates: CheckinDate[],
  excludeRef?: string,
) =>
  object().shape({
    date: yupDate()
      .typeError('Date is required')
      .required('Date is required')
      .test('unique', 'A check-in date already exists for this day', (v) => {
        if (!v) return true;
        const key = v.toLocaleDateString('en-CA');
        return !existingDates.some(
          (d) => d.ref !== excludeRef && localDateKey(d.date) === key,
        );
      }),
  });

// "In: X | Out: Y | Total: Z" stats row for each check-in date list item.
// Uses MUI's `<Divider orientation='vertical' flexItem />` between stats
// instead of inline `{' \u00b7 '}` strings so the separator follows the
// design-system convention.
//
// In/Out counts are filtered against the live (non-soft-deleted) attendee
// set from FirebaseContext. Without this filter, refs left behind in
// `checkedInRefs` / `checkedOutRefs` after an attendee is soft-deleted
// would still inflate the counts even though the matching rows aren't
// visible anywhere else in the UI.
//
// Total reflects the event's full live attendee roster, not the count of
// kids who happened to check in on this date — context.attendees is already
// scoped to the active event by `getAttendeesForEventQuery` and excludes
// soft-deletes.
export const CheckInDateStats = ({ date }: { date: CheckinDate }) => {
  const { attendees, activeEvent } = useFirebaseContext();
  // With the event's `hideCheckout` flag on, "In" means "came that day"
  // (raw check-ins) and the "Out" stat is dropped entirely — check-out
  // isn't part of the workflow for such events.
  const hideCheckout = Boolean(activeEvent?.hideCheckout);
  const liveRefs = new Set(attendees.map((a) => a.ref));
  const checkedIn = (date.checkedInRefs ?? []).filter((r) => liveRefs.has(r))
    .length;
  const out = (date.checkedOutRefs ?? []).filter((r) => liveRefs.has(r))
    .length;
  const stillIn = checkedIn - out;
  const total = attendees.length;
  return (
    <Stack
      direction='row'
      spacing={1}
      divider={<Divider orientation='vertical' flexItem />}
      sx={{ color: 'text.secondary', flexGrow: 1 }}
    >
      <Typography variant='body2'>
        In: {hideCheckout ? checkedIn : stillIn}
      </Typography>
      {!hideCheckout && <Typography variant='body2'>Out: {out}</Typography>}
      <Typography variant='body2'>Total: {total}</Typography>
    </Stack>
  );
};
